/**
 * Protocol Guide - Step-by-step EMR guidance with ghost paths
 * Adapted from PRISM Chrome Extension for React/Spectra demo
 */

import emrPointer from './EMRPointer';

class ProtocolGuide {
  constructor() {
    this.activeProtocol = null;
    this.activeProtocolSteps = [];
    this.guideActive = false;
    this.currentGuideStep = 0;
    this.guideSteps = [];
  }

  /**
   * Set active protocol and start guide
   * @param {Object} protocol - Protocol object with name, description
   * @param {Array<string>} steps - Array of step strings
   */
  startProtocolGuide(protocol, steps) {
    this.activeProtocol = protocol;
    this.activeProtocolSteps = steps || [];
    this.guideActive = true;
    this.currentGuideStep = 0;
    
    // Parse steps into guide format
    if (steps && steps.length > 0) {
      this.guideSteps = this.parseProtocolSteps(steps);
    } else {
      // Fallback demo steps
      this.guideSteps = [
        {
          title: 'Step 1: Review Medications',
          message: 'Check the patient\'s current medications section',
          selector: 'MEDICATIONS',
          waitForClick: false
        },
        {
          title: 'Step 2: Check Active Problems',
          message: 'Review the problem list for relevant conditions',
          selector: 'ACTIVE PROBLEMS',
          waitForClick: false
        }
      ];
    }
    
    console.log('[ProtocolGuide] Starting guide with', this.guideSteps.length, 'steps');
    this.showGuideStep(0);
  }

  /**
   * Parse protocol steps into guide format
   * @param {Array<string>} steps - Raw protocol steps
   * @returns {Array<Object>} Parsed guide steps
   */
  parseProtocolSteps(steps) {
    const guideSteps = [];
    let stepNumber = 0;
    
    for (const stepText of steps) {
      // Skip substeps (indented lines) for now - we'll handle them later
      const isSubstep = stepText.startsWith('  ') || stepText.startsWith('\t');
      if (isSubstep) continue;
      
      stepNumber++;
      const parsed = this.parseStep(stepText, stepNumber);
      guideSteps.push(parsed);
    }
    
    return guideSteps;
  }

  /**
   * Parse a single step and extract selector
   * @param {string} stepText - Step text
   * @param {number} stepNum - Step number
   * @returns {Object} Parsed step
   */
  parseStep(stepText, stepNum) {
    let selector = null;
    const lowerStep = stepText.toLowerCase();
    
    // Determine if this is a form field vs button step
    const isFormFieldStep = lowerStep.includes('type') || lowerStep.includes('enter') || 
                           lowerStep.includes('fill') || lowerStep.includes('input');
    
    const isButtonStep = !isFormFieldStep && (
      lowerStep.includes('click') || lowerStep.includes('add') || 
      lowerStep.includes('create')
    );
    
    // Map keywords to EMR sections/buttons
    if (lowerStep.includes('vital') || lowerStep.includes('blood pressure')) {
      selector = isButtonStep ? 'Add Vital' : 'VITAL SIGNS';
    } else if (lowerStep.includes('medication') || lowerStep.includes('prescription')) {
      selector = isButtonStep ? 'Add Medication' : 'MEDICATIONS';
    } else if (lowerStep.includes('problem') || lowerStep.includes('diagnosis')) {
      selector = isButtonStep ? 'Add Problem' : 'ACTIVE PROBLEMS';
    } else if (lowerStep.includes('insurance') || lowerStep.includes('coverage')) {
      selector = isButtonStep ? 'Add Insurance' : 'INSURANCE';
    } else if (lowerStep.includes('barrier') || lowerStep.includes('sdoh')) {
      selector = isButtonStep ? 'Add Barrier' : 'BARRIERS TO CARE';
    } else if (lowerStep.includes('appointment') || lowerStep.includes('schedule')) {
      selector = isButtonStep ? 'Add Appointment' : 'APPOINTMENTS';
    } else if (lowerStep.includes('program') || lowerStep.includes('enroll')) {
      selector = isButtonStep ? 'Add Program' : 'PROGRAMS';
    } else if (lowerStep.includes('allerg')) {
      selector = isButtonStep ? 'Add Allergy' : 'ALLERGIES & MEDS';
    } else if (lowerStep.includes('assessment') || lowerStep.includes('screening')) {
      selector = isButtonStep ? 'Add Assessment' : 'ASSESSMENTS & SCREENINGS';
    } else if (lowerStep.includes('resource') || lowerStep.includes('referral')) {
      selector = isButtonStep ? 'Add Resource' : 'RESOURCES PROVIDED';
    }
    
    return {
      title: `Step ${stepNum}`,
      message: stepText,
      selector: selector,
      waitForClick: isButtonStep
    };
  }

  /**
   * Show a specific guide step
   * @param {number} stepIndex - Step index to show
   */
  showGuideStep(stepIndex) {
    if (stepIndex >= this.guideSteps.length) {
      this.completeGuide();
      return;
    }
    
    const step = this.guideSteps[stepIndex];
    console.log('[ProtocolGuide] Showing step', stepIndex + 1, 'of', this.guideSteps.length, step);
    
    if (!step.selector) {
      // No selector - show generic toast
      this.showFallbackToast(stepIndex, step);
      return;
    }
    
    // Use EMR pointer to show step
    const result = emrPointer.pointTo(
      step.selector,
      step.title,
      step.message,
      {
        showButton: !step.waitForClick,  // Show "Got it" for info steps
        detectClick: step.waitForClick,  // Auto-advance on click for action steps
        onClose: () => {
          this.currentGuideStep++;
          setTimeout(() => {
            this.showGuideStep(this.currentGuideStep);
          }, 500);
        }
      }
    );
    
    if (!result) {
      // Element not found - show fallback
      console.warn('[ProtocolGuide] Element not found for selector:', step.selector);
      this.showFallbackToast(stepIndex, step);
    }
  }

  /**
   * Show fallback toast when element can't be found
   * @param {number} stepIndex - Step index
   * @param {Object} step - Step object
   */
  showFallbackToast(stepIndex, step) {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      max-width: 400px;
      background: rgba(18, 24, 54, 0.95);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(162, 134, 201, 0.3);
      border-radius: 12px;
      padding: 20px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      z-index: 9999999;
      animation: slideIn 0.3s ease;
    `;
    
    const escapeHTML = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };
    
    toast.innerHTML = `
      <div style="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: rgba(162, 134, 201, 1);">
        ${escapeHTML(step.title)} (${stepIndex + 1}/${this.guideSteps.length})
      </div>
      <div style="font-size: 13px; margin-bottom: 16px; line-height: 1.5;">
        ${escapeHTML(step.message)}
      </div>
      <div style="display: flex; gap: 8px;">
        <button style="
          flex: 1;
          background: rgba(162, 134, 201, 0.3);
          border: 2px solid rgba(162, 134, 201, 0.6);
          color: white;
          padding: 10px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s;
        " onmouseover="this.style.background='rgba(162, 134, 201, 0.5)'" onmouseout="this.style.background='rgba(162, 134, 201, 0.3)'" onclick="
          this.parentElement.parentElement.remove();
          window.prismProtocolGuide.currentGuideStep++;
          window.prismProtocolGuide.showGuideStep(window.prismProtocolGuide.currentGuideStep);
        ">
          Next Step →
        </button>
        <button style="
          background: transparent;
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: white;
          padding: 10px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s;
        " onmouseover="this.style.borderColor='rgba(255, 255, 255, 0.5)'" onmouseout="this.style.borderColor='rgba(255, 255, 255, 0.3)'" onclick="
          this.parentElement.parentElement.remove();
          window.prismProtocolGuide.stopProtocolGuide();
        ">
          ✕
        </button>
      </div>
    `;
    
    document.body.appendChild(toast);
  }

  /**
   * Complete the guide
   */
  completeGuide() {
    console.log('[ProtocolGuide] Guide completed');
    
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      max-width: 400px;
      background: linear-gradient(135deg, rgba(101, 169, 196, 0.95) 0%, rgba(91, 158, 130, 0.95) 100%);
      border-radius: 12px;
      padding: 20px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      z-index: 9999999;
      animation: slideIn 0.3s ease;
    `;
    
    toast.innerHTML = `
      <div style="text-align: center;">
        <div style="font-size: 48px; margin-bottom: 12px;">🎉</div>
        <div style="font-size: 16px; font-weight: 600; margin-bottom: 8px;">
          Protocol Guide Complete!
        </div>
        <div style="font-size: 13px; margin-bottom: 16px; opacity: 0.9;">
          You've completed all steps for ${this.activeProtocol?.name || 'this protocol'}.
        </div>
        <button style="
          width: 100%;
          background: white;
          color: rgba(91, 158, 130, 1);
          border: none;
          border-radius: 6px;
          padding: 10px;
          font-weight: 600;
          cursor: pointer;
          font-size: 13px;
        " onclick="this.parentElement.parentElement.remove()">
          Done
        </button>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (toast.parentNode) toast.remove();
    }, 5000);
    
    this.stopProtocolGuide();
  }

  /**
   * Stop protocol guide
   */
  stopProtocolGuide() {
    this.guideActive = false;
    this.currentGuideStep = 0;
    this.guideSteps = [];
    emrPointer.clear();
    console.log('[ProtocolGuide] Guide stopped');
  }

  /**
   * Get guide state
   * @returns {Object} Guide state
   */
  getGuideState() {
    return {
      active: this.guideActive,
      currentStep: this.currentGuideStep,
      totalSteps: this.guideSteps.length,
      protocol: this.activeProtocol
    };
  }
}

// Create singleton instance
const protocolGuide = new ProtocolGuide();

// Expose globally for debugging and toast callbacks
if (typeof window !== 'undefined') {
  window.prismProtocolGuide = protocolGuide;
}

export default protocolGuide;
